/**
 * /reset?token=... — unauthenticated password-reset confirmation.
 *
 * Reads the raw token from the URL query, posts it with the new
 * password to the confirm endpoint, redirects to /login on success.
 * Every token-failure branch (missing / expired / used / invalidated)
 * maps to the same `invalid_token` response and the same friendly UI
 * — no leak about lifecycle state.
 */
import { Link, createRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import {
  evaluatePasswordRules,
  validatePasswordStrength,
  type PasswordStrengthReason,
} from '@kora/types';
import { rootRoute } from './root.js';
import { confirmPasswordReset } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

const SearchSchema = z.object({
  token: z.string().optional(),
});

export const resetConfirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset',
  validateSearch: (search) => SearchSchema.parse(search),
  component: ResetConfirmPage,
});

interface FormValues {
  newPassword: string;
  confirmPassword: string;
}

const FormSchema = z
  .object({
    newPassword: z.string().min(1, 'Required'),
    confirmPassword: z.string().min(1, 'Required'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match.',
  });

function ResetConfirmPage(): JSX.Element {
  const search = useSearch({ from: resetConfirmRoute.id });
  const navigate = useNavigate();
  const token = (search.token ?? '').trim();
  const [serverError, setServerError] = useState<{
    field: 'newPassword' | 'form';
    message: string;
  } | null>(null);
  const [done, setDone] = useState(false);

  // No-token short-circuit — show the friendly invalid-link copy and
  // a way to request a new one.
  if (!token) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="w-full max-w-sm space-y-6 p-8 text-center">
          <h1 className="font-serif text-3xl">LumoHR</h1>
          <Alert variant="error">Reset link is invalid. Request a new one from the login page.</Alert>
          <Link
            to="/reset/request"
            className="inline-block rounded bg-white text-black font-medium py-2 px-4"
          >
            Request a new link
          </Link>
        </div>
      </main>
    );
  }

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');
  const rules = evaluatePasswordRules(newPassword);
  const allRulesPass = rules.every((r) => r.satisfied);
  const matches = newPassword.length > 0 && newPassword === confirmPassword;

  const mutation = useMutation({
    mutationFn: confirmPasswordReset,
    onSuccess: () => {
      setDone(true);
      // Short delay so the user reads the success message before redirect.
      setTimeout(() => {
        void navigate({ to: '/login', search: { reset: 'success' } });
      }, 1800);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        const body = err.body as {
          error?: string;
          reason?: PasswordStrengthReason;
        } | null;
        if (err.status === 400 && body?.error === 'weak_password') {
          setServerError({
            field: 'newPassword',
            message: weakPasswordLabel(body.reason),
          });
          return;
        }
        if (err.status === 400 && body?.error === 'invalid_token') {
          setServerError({
            field: 'form',
            message:
              'This reset link is no longer valid. Request a new one from the login page.',
          });
          return;
        }
        if (err.status === 429) {
          setServerError({
            field: 'form',
            message: 'Too many attempts. Wait a few minutes and try again.',
          });
          return;
        }
      }
      setServerError({
        field: 'form',
        message: 'Could not reset your password. Try again.',
      });
    },
  });

  async function onSubmit(values: FormValues): Promise<void> {
    setServerError(null);
    // Defensive: re-run the strength check locally so a stale
    // indicator can't smuggle a weak password to the server.
    const strength = validatePasswordStrength(values.newPassword);
    if (!strength.ok) {
      setServerError({
        field: 'newPassword',
        message: weakPasswordLabel(strength.reason),
      });
      return;
    }
    mutation.mutate({ token, newPassword: values.newPassword });
  }

  const canSubmit = allRulesPass && matches && !isSubmitting;

  if (done) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="w-full max-w-sm space-y-4 p-8 text-center">
          <h1 className="font-serif text-3xl">LumoHR</h1>
          <div
            role="status"
            className="rounded border border-green-900 bg-green-950/40 p-4 text-sm text-green-300"
          >
            Password reset. Redirecting to sign in…
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="w-full max-w-sm space-y-6 p-8">
        <header className="text-center space-y-2">
          <h1 className="font-serif text-3xl">LumoHR</h1>
          <p className="text-sm text-gray-400">Set a new password</p>
        </header>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Field
            id="newPassword"
            label="New password"
            type="password"
            autoComplete="new-password"
            error={
              errors.newPassword?.message ??
              (serverError?.field === 'newPassword' ? serverError.message : undefined)
            }
            inputProps={register('newPassword')}
          />
          <ul className="text-xs space-y-1">
            {rules.map((r) => (
              <li
                key={r.rule}
                className={r.satisfied ? 'text-green-400' : 'text-gray-500'}
              >
                {r.satisfied ? '✓' : '○'} {r.label}
              </li>
            ))}
            <li
              className={
                matches
                  ? 'text-green-400'
                  : confirmPassword.length > 0
                    ? 'text-red-400'
                    : 'text-gray-500'
              }
            >
              {matches ? '✓' : '○'} Confirmation matches new password
            </li>
          </ul>
          <Field
            id="confirmPassword"
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            inputProps={register('confirmPassword')}
          />

          {serverError?.field === 'form' ? (
            <Alert variant="error">
              {serverError.message}
              {serverError.message.includes('no longer valid') ? (
                <>
                  {' '}
                  <Link to="/reset/request" className="underline">
                    Request a new link
                  </Link>
                  .
                </>
              ) : null}
            </Alert>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded bg-white text-black font-medium py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Resetting…' : 'Reset password'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400">
          <Link to="/login" className="underline hover:text-white">
            Back to sign in
          </Link>
        </p>
      </div>
    </main>
  );
}

function weakPasswordLabel(reason: PasswordStrengthReason | undefined): string {
  switch (reason) {
    case 'too_short':
      return 'Password must be at least 12 characters long.';
    case 'no_letter':
      return 'Password must contain at least one letter.';
    case 'no_number':
      return 'Password must contain at least one number.';
    default:
      return 'Password does not meet the strength requirements.';
  }
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  autoComplete?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<FormValues>>['register']>;
}

function Field({
  id,
  label,
  type = 'text',
  autoComplete,
  error,
  inputProps,
}: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
